import type {
  Contract,
  ContractInstallment,
} from '~/store/contracts/interfaces';

export interface InstallmentAmortization {
  estimatedInterestCents: number;
  principalPortionCents: number;
  paidAmountCents: number;
  interestDiscountCents: number;
  interestPaidCents: number;
}

export function computeInstallmentAmortization(
  contract: Pick<Contract, 'amountTotal' | 'amountFinanced'>,
  installment: Pick<
    ContractInstallment,
    'amount' | 'status' | 'paidAmount' | 'principal' | 'interest'
  >,
): InstallmentAmortization {
  const hasRealSplit =
    installment.interest != null && installment.principal != null;

  let estimatedInterestCents: number;
  let principalPortionCents: number;
  if (hasRealSplit) {
    estimatedInterestCents = installment.interest as number;
    principalPortionCents = installment.principal as number;
  } else {
    const total = contract.amountTotal || 0;
    const contractInterest = Math.max(
      0,
      contract.amountTotal - contract.amountFinanced,
    );
    const share = total > 0 ? installment.amount / total : 0;
    estimatedInterestCents = Math.round(contractInterest * share);
    principalPortionCents = Math.max(
      0,
      installment.amount - estimatedInterestCents,
    );
  }

  const isPaid = installment.status === 'paid';
  const paidAmountCents = isPaid
    ? (installment.paidAmount ?? installment.amount)
    : installment.amount;
  const interestDiscountCents = isPaid
    ? Math.max(0, installment.amount - paidAmountCents)
    : 0;
  const interestPaidCents = isPaid
    ? Math.max(0, paidAmountCents - principalPortionCents)
    : 0;

  return {
    estimatedInterestCents,
    principalPortionCents,
    paidAmountCents,
    interestDiscountCents,
    interestPaidCents,
  };
}

export interface ContractAmortizationTotals {
  interestPaidCents: number;
  interestDiscountCents: number;
  outstandingPrincipalCents: number;
}

export function computeContractAmortization(
  contract: Pick<Contract, 'amountTotal' | 'amountFinanced'>,
  installments: ContractInstallment[],
): ContractAmortizationTotals {
  return installments.reduce<ContractAmortizationTotals>(
    (acc, inst) => {
      const a = computeInstallmentAmortization(contract, inst);
      if (inst.status === 'paid') {
        return {
          ...acc,
          interestPaidCents: acc.interestPaidCents + a.interestPaidCents,
          interestDiscountCents:
            acc.interestDiscountCents + a.interestDiscountCents,
        };
      }
      return {
        ...acc,
        outstandingPrincipalCents:
          acc.outstandingPrincipalCents + a.principalPortionCents,
      };
    },
    {
      interestPaidCents: 0,
      interestDiscountCents: 0,
      outstandingPrincipalCents: 0,
    },
  );
}
